import { useMemo, useContext, useEffect, useCallback, useState } from "react"
import qs from "qs"

import { CenterModalCallbackContext } from "context/popup/CenterModalProvider"
import { ToastCallbackContext } from "context/popup/ToastProvider"
import { AnimatedWindowCallbackContext } from "context/window/AnimatedWindowProvider"

import server from "server/serverController2"
import { copyTxt, redirectExternal } from "utils/utils"

import ConfirmCenterModal from "components/presenters/popup/ConfirmCenterModal"
import OrderShippingTracking from "components/presenters/commerce/order/orderHistoryList/OrderShippingTracking"
import OrderHistoryDetail from "components/containers/commerce/order/OrderHistoryDetail"

const useOrderHistory = () => {
	const { openCenterModal, closeCenterModal, ref } = useContext(CenterModalCallbackContext)
	const { openToast } = useContext(ToastCallbackContext)
	const { openWindow, id } = useContext(AnimatedWindowCallbackContext)

	const [isLoading, setIsLoading] = useState(false)
	const [isEnd, setIsEnd] = useState(false)
	const [orders, setOrders] = useState([])
	const [lastPosNo, setLastPosNo] = useState(0)

	const loadOrderHistory = useMemo(async () => {
		try {
			const queryString = qs.stringify({
				show_order_items: true,
				is_show_shipping_data: true,
				is_show_payment_data: true,
				limit: 20,
				order_status: "paid",
				...(lastPosNo && { last_pos_no: lastPosNo }),
			})

			const response = await server.getRequestData(`users/orders?${queryString}`)
			if (response.success !== 1) {
				const errData = response.error.res_data
				throw errData.detail_code
			}

			const { orders } = response.data

			return orders
		} catch (error) {
			console.log(error)
			setIsLoading(false)
		}
	}, [lastPosNo])

	const handleSetInitData = useCallback(async () => {
		const orderHistory = await loadOrderHistory

		if (orderHistory.length < 20) {
			setIsEnd(true)
		}
		if (orderHistory.length > 0) {
			setLastPosNo(orderHistory[orderHistory.length - 1].no)
			setOrders((orderList) => [...orderList, ...orderHistory])
		}

		setIsLoading(false)
	}, [loadOrderHistory])

	const handleOpenKakao = useCallback(
		(id) => {
			redirectExternal("http://pf.kakao.com/_gmtJs/chat")
			closeCenterModal(id)
		},
		[closeCenterModal]
	)

	const openConfirmModal = useCallback(
		(dataList) => {
			openCenterModal({
				Component: (
					<ConfirmCenterModal
						whiteboxRef={ref}
						dataList={dataList}
						onCancel={(id) => closeCenterModal(id)}
						onComplete={handleOpenKakao}
					/>
				),
			})
		},
		[closeCenterModal, handleOpenKakao, openCenterModal, ref]
	)

	const handleClickOrderDetail = useCallback(
		(orderNo) => {
			openWindow({
				id,
				Component: <OrderHistoryDetail orderNo={orderNo} />,
				headerProps: {
					title: "주문상세",
				},
			})
		},
		[openWindow, id]
	)

	const handleClickCopy = useCallback(
		(id, trackingNo) => {
			closeCenterModal(id)
			copyTxt(trackingNo)
			openToast({ iconName: "checkCircle", desc: "복사되었어요", delay: 2000 })
		},
		[closeCenterModal, openToast]
	)

	const handleClickTrackingNumber = useCallback(
		(trackingNo, courierName) => {
			openCenterModal({
				Component: (
					<OrderShippingTracking
						whiteboxRef={ref}
						courierName={courierName}
						trackingNo={trackingNo}
						onClose={(id) => closeCenterModal(id)}
						onCopy={handleClickCopy}
					/>
				),
			})
		},
		[handleClickCopy, openCenterModal, closeCenterModal, ref]
	)

	const handleClickCancel = useCallback(() => {
		const dataList = {
			title: "주문 취소",
			description: "주문 취소는 카카오 문의에서\n가능해요.",
		}
		openConfirmModal(dataList)
	}, [openConfirmModal])

	const handleClickRefund = useCallback(() => {
		const dataList = {
			title: "교환/반품",
			description: "교환/반품은 카카오 문의에서\n가능해요.",
		}
		openConfirmModal(dataList)
	}, [openConfirmModal])

	const getFooterActionProps = useCallback(
		(orderItem) => {
			if (orderItem.order_status === "paid") {
				if (orderItem.tracking_no) {
					return {
						onActionClick: handleClickRefund,
						onDetailClick: () =>
							handleClickTrackingNumber(
								orderItem.tracking_no,
								orderItem.courier_name
							),
						actioBtnTxt: "교환 / 반품",
						detailBtnTxt: "송장 번호",
					}
				}

				return {
					onActionClick: handleClickCancel,
					actioBtnTxt: "주문 취소",
				}
			} else if (orderItem.order_status === "canceled") {
				return {
					onDetailClick: () => {},
					detailBtnTxt: "주문 취소 상세",
				}
			} else if (orderItem.order_status === "cancel_requested") {
				return {
					onActionClick: () => {},
					actioBtnTxt: "주문 취소 철회",
				}
			} else if (orderItem.order_status === "refunded") {
				return {
					onDetailClick: handleClickOrderDetail,
					detailBtnTxt: "교환 / 반품 상세",
				}
			} else if (orderItem.order_status === "refund_requested") {
				return {
					onActionClick: () => {},
					actioBtnTxt: "교환 / 반품 철회",
				}
			}
		},
		[handleClickCancel, handleClickRefund, handleClickOrderDetail, handleClickTrackingNumber]
	)

	useEffect(() => {
		if (!isLoading && !isEnd) {
			setIsLoading(true)
			handleSetInitData()
		}
	}, [isLoading, isEnd, handleSetInitData])

	return { orders, getFooterActionProps, handleClickOrderDetail }
}

export default useOrderHistory
